import { useEffect, useRef, useState } from "react";
import { useOverLimitTrigger } from "@/pages/dashboard/components/OverLimitTip";
import { getLocale, shallowEqual, useSelector } from "umi";
import { downLoadPcConsoleV3DayPerformanceTable, getPcConsoleV3DayPerformanceTable } from "@/pages/dashboard/services/overview";
import { LANGUAGE_MAP, PERIODTIMEOBJ } from "@/pages/dashboard/_config/constant";
import { IsSingleDay, formatFilterValues } from "@/pages/dashboard/utils/utils";
import { PagingQuery } from "tea-component";
import { getTableRequestParams } from "./hooks";
import { ComponentMetricsItemType } from "@/pages/dashboard/components/PageContainer/type";
import { SortBy } from "tea-component/lib/table/addons";

const useData = (
  filterValue: any,
  filterLoading: boolean,
  selectType: string,
  config: {
    api: any;
    componentMetrics: ComponentMetricsItemType;
    dataKey: string;
    tableKey: string;
    hasReqMetrics: boolean;
  },
  data: any
  // getRequestParams: Function,
) => {
  const { api: apiObj, dataKey, tableKey, hasReqMetrics, componentMetrics } = config || {};
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  // // 分页
  const [paging, setPaging] = useState<PagingQuery>({ pageSize: 10, pageIndex: 1 });
  // // 排序
  const [sort, setSort] = useState<SortBy[]>([]);
  // 总条数
  const [total, setTotal] = useState(0);
  const [frontLogicalFlag, setFrontLogicalFlag] = useState<string | undefined>(undefined);
  const { overLimitBarTriggerKey, handleResponse } = useOverLimitTrigger();

  // 表格数据
  const [tableData, setTableData] = useState([]);

  // 聚合数据
  const [statisticsData, setStatisticsData] = useState([]);

  const [statisticDateTimeTypeState, setStatisticDateTimeTypeState] = useState('Day');


  // const [,forceUpdate] = useReducer(x=>x+1,0)
  const filterValueSort = useRef(filterValue);
  const accumulateOldValueRef = useRef<string[]>(dataKey === 'event_comparison'?[]:['date']);

  const gameCode = useSelector((state: any) => state?.dashboard?.currentGame?.game_code, shallowEqual);
  // 获取表格上传参数
  const getRequestParams = (paging: any, sort: any, filterValue: any, otherParams?: any) => {

    // overview页面的日期上传参数逻辑与其他页面不同, 故特殊处理
    const { timeType, diff, rangeTime, comparisonDate, metrics, contrastDate, statisticDate, statisticDate_timeType = "Day", metric, selector,...filters } = filterValue || {};
    setStatisticDateTimeTypeState(statisticDate_timeType);
    const periodObj = { period: PERIODTIMEOBJ?.[timeType] || 'daily' };
    // 是否取消勾选date维度
    const hasDate = accumulateOldValueRef?.current?.includes('date');
    // 是否单日单周单月
    const isSingle = false;
    // 是否累计值计算逻辑
    const isAccumulated = !hasDate && !isSingle;

    // 传参兜底筛选器参数
    let params = {
      gameCode,
      accumulated: accumulateOldValueRef?.current,
      sort,
      paging,
      selectType,
      dataKey,
      filters: formatFilterValues(filters),
      ...otherParams,
    };

    // 事件对比页面接口请求
    if (dataKey === 'event_comparison') {
      let reqMetrics = isAccumulated && metrics?.includes('active_users') ? metrics?.map((w: any) => {
        if (w === 'active_users') return 'avg_dau';
        return w;
      }) || metrics || [] : metrics || [];

      const metricKeyMap: Record<string, string> = {};
      const cmList = Array.isArray(componentMetrics) ? componentMetrics : [];
      cmList.forEach((item: any) => {
        if (item?.metricKey && item?.reqMetricKey && item.metricKey !== item.reqMetricKey) {
          metricKeyMap[item.metricKey] = item.reqMetricKey;
        }
      });
      reqMetrics = reqMetrics.map((key: string) => metricKeyMap[key] || key);

      params = {
        ...params,
        filterValue,
        comparison_date: comparisonDate,
        metrics: reqMetrics,
        ...periodObj
      };
    }
    // 时间对比页面接口请求
    if (dataKey === 'date_comparison') {
      params = {
        ...params,
        period: PERIODTIMEOBJ?.[statisticDate_timeType] || 'daily',
        ct_end_time: contrastDate?.[1] || '',
        ct_start_time: contrastDate?.[0] || '',
        st_end_time: statisticDate?.[1] || '',
        st_start_time: statisticDate?.[0] || '',
      };

      // 在这里添加对 dataFrame 的判断
      if (data && data.dataFrame === "pc_inflextion") {
        params.metric = tableKey === "retention" ? metric : data?.metrics?.metric_key;
      }
      if (tableKey === "retention") { // 注意这里的修改
        params.retention_type = data?.metrics?.metric_key;
        params.ct_end_time = contrastDate || '';
        params.ct_start_time = contrastDate || '';
        params.st_end_time = statisticDate || '';
        params.st_start_time = statisticDate || '';
      }

      // 获取对应颗粒度的宽表字段配置
      // 优先级：data.reqMetrics > data.tableMetric > 远程 componentMetrics
      if (data?.reqMetrics) {
        const rm = data.reqMetrics;
        params.metrics = Array.isArray(rm) ? rm : (rm[statisticDate_timeType] || rm.Day || []);
      } else if (data?.tableMetric && Array.isArray(data.tableMetric) && data.tableMetric.length > 0) {
        params.metrics = data.tableMetric;
      } else {
        const comps = componentMetrics?.[0]?.[timeType];
        if(hasReqMetrics && comps){
          params.metrics = comps?.map(item=> item.metricKey);
        }
      }

      // 取消分日期时，将 active_users 替换为对应颗粒度的 avg 指标
      if (isAccumulated && params.metrics) {
        const avgMetricMap: Record<string, string> = {
          Day: 'avg_dau',
          Week: 'avg_wau',
          Month: 'avg_mau',
        };
        const avgMetric = avgMetricMap[statisticDate_timeType] || 'avg_dau';
        params.metrics = params.metrics.map((m: string) => m === 'active_users' ? avgMetric : m);
      }
    }

    return getTableRequestParams(params);
  };


  useEffect(() => {
    filterValueSort.current = { filterValue, paging };
  }, [filterValue, paging, sort]);

  // 点击筛选器的时候 || metrics更新的时候
  useEffect(() => {
    if (!filterLoading) {
      setPaging({ pageIndex: 1, pageSize: paging.pageSize });
      setSort([]);
      const params = getRequestParams({ pageIndex: 1, pageSize: paging.pageSize }, [], filterValue);
      getTableData(params);
    }
  }, [filterValue, filterLoading, data?.metrics]);


  const getTableData = (params: any) => {
    setIsError(false);
    setLoading(true);
    const api = `${apiObj?.tableApi}?language=${LANGUAGE_MAP[getLocale()]}`;

    getPcConsoleV3DayPerformanceTable(api, params).then((resp) => {
      setLoading(false);
      setTableData(resp.metrics_list || []);
      setStatisticsData(resp.aggregation || []);
      // 设置总条数
      setTotal(resp.row_count);
      setFrontLogicalFlag(resp?.front_logical_flag);
      handleResponse(resp?.front_logical_flag);
    }).catch(e => {
      setLoading(false);
      setIsError(true);
      console.log("overview 宽表请求错误: ", filterValue.timeType, e);
    });
  };


  return {
    tableData,
    statisticsData,
    paging,
    loading,
    isError,
    sort,
    total,
    frontLogicalFlag,
    overLimitBarTriggerKey,
    filterValueSort,
    accumulateOldValueRef,
    isSingle: IsSingleDay({ ...filterValue, period: PERIODTIMEOBJ[filterValue?.timeType] }),
    dataKey,
    tableKey,
    setPaging,
    setSort,
    getRequestParams,
    getData: getTableData,
    downLoadData: downLoadPcConsoleV3DayPerformanceTable,
    statisticDate_timeType: statisticDateTimeTypeState,
  };
};

export default useData;